import SchemaValidator from "./schema-validator";
import { Static } from "@sinclair/typebox";
import { Value } from "@sinclair/typebox/value";

export class BaseApiClientError extends Error { }

export class ApiClientError extends BaseApiClientError {
	readonly cause?: Error;

	constructor(cause?: Error) {
		super(`API Client Error: ${cause?.message ?? "unknown"}`);
		this.name = `ApiClientError: ${cause?.name ?? "unknown"}`;
		this.cause = cause;
	}
}

export class ApiClientResponseError extends BaseApiClientError {
	readonly status: number;
	readonly originalMessage?: string;
	readonly code?: string;
	readonly details?: unknown;

	constructor(status: number, { code, message, details }: { code?: string; message?: string; details?: unknown } = {}) {
		super(`API Client Error: ${code} (${status}): ${message}`);
		this.name = `ApiClientError: ${code} (${status}): ${message}`;
		this.status = status;
		this.originalMessage = message;
		this.code = code;
		this.details = details;
	}
}

export class ApiClientSchemaError extends BaseApiClientError {
	readonly schema: SchemaValidator;
	readonly value: unknown;

	constructor(schema: SchemaValidator, value: unknown) {
		super(`API Client Error: ${JSON.stringify([...Value.Errors(schema.schema, value)], undefined, 4)}`);
		this.name = "ApiClientError: schema";
		this.schema = schema;
		this.value = value;
	}
}

export class UnauthenticatedError extends BaseApiClientError {
	static NAME = "UnauthenticatedError";

	constructor() {
		super(UnauthenticatedError.NAME);
		this.name = UnauthenticatedError.NAME;
	}
}

export async function handleApiError(promise: Promise<Response>) {
	try {
		const response = await promise;
		if (response.ok) {
			return response;
		} else if (response.status === 401 || response.status === 403) {
			throw new UnauthenticatedError();
		} else {
			const errorMessage = await response.text();
			throw new ApiClientResponseError(
				response.status,
				errorMessage.includes("<!DOCTYPE html>") ? undefined : JSON.parse(errorMessage),
			);
		}
	} catch (e) {
		if (e instanceof Error) {
			if (e instanceof BaseApiClientError) {
				throw e;
			}
			throw new ApiClientError(e);
		}
	}
	throw new ApiClientError();
}

export async function handleApiErrorWithSchema<SV extends SchemaValidator>(
	promise: Promise<Response>,
	schemaValidator: SV,
): Promise<Static<SV["schema"]>> {
	const response = await handleApiError(promise);
	const data: unknown = await response.json();
	if (schemaValidator.validate(data)) {
		return data;
	}
	throw new ApiClientSchemaError(schemaValidator, data);
}

export function withDefaultErrorHandler<T extends object>(instance: T): T {
	return new Proxy(instance, {
		get(target, prop, receiver) {
			const orig = Reflect.get(target, prop, receiver);
			if (typeof orig === "function") {
				return async (...args: unknown[]) => {
					try {
						return await orig.apply(this, args) as unknown;
					} catch (err) {
						if (err instanceof Error) throw new ApiClientError(err);
						throw new ApiClientError(new Error(`Unknown error: ${JSON.stringify(err)}`));
					}
				};
			}
			return orig;
		}
	});
}
